import i18next, { TFunction } from 'i18next';
import {
  BookOpenTextIcon,
  FileTextIcon,
  FolderIcon,
  GlobeIcon,
  MessagesSquareIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { documentsEndpoints } from '@/api/endpoints';
import { trackEvent } from '@/segment/segment';
import {
  CollectionDocument,
  Datasource,
  DatasourceType,
  Document,
  ZendeskConfig,
} from '@/shared/types/collections';
import { EventName } from '@/shared/types/segment';
import { downloadCsv } from '@/util/fileManager';

import { DatasourceDocument, DatasourceTab } from './types';

/**
 * Extracts the domain name from a URL string, removing the 'www.' prefix if present.
 *
 * @param {string} rawUrl - The URL string to extract the domain from
 * @returns {string | null} The extracted domain name without 'www.' prefix, or null if the URL is invalid
 * @throws {TypeError} If the input is not a valid URL string
 *
 * @example
 * // Returns 'example.com'
 * extractDomainName('https://www.example.com')
 *
 */
export function extractDomainName(rawUrl: string): string | null {
  return new URL(rawUrl).hostname.replace('www.', '');
}

/**
 * Helper function to ensure URL has a protocol
 * @param url - The URL to check and modify
 * @returns URL with protocol (https:// if none present)
 *
 * @example
 * // Returns 'https://example.com'
 * ensureProtocol('example.com')
 *
 */
export function ensureProtocol(url: string): string {
  if (!url) {
    return url;
  }

  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return trimmedUrl;
  }

  // Check if URL already has a protocol
  if (!/^https?:\/\//i.test(trimmedUrl)) {
    return `https://${trimmedUrl}`;
  }

  return trimmedUrl;
}

/**
 * Gets the translated status text for a datasource based on its status
 */
export function getDatasourceStatusText(status: string, is_draft: boolean, t: TFunction): string {
  if (status === 'failed') {
    return t('collections.datasources.failed');
  } else if (status === 'available') {
    if (is_draft) {
      return t('collections.is_draft');
    }
    return t('collections.published');
  } else if (status === 'invalid') {
    return t('collections.invalid');
  } else if (status === 'processing') {
    return t('collections.processing');
  } else if (status === 'pending') {
    return t('collections.pending');
  } else if (status === 'indexing') {
    return t('collections.indexing');
  }
}

/**
 * Returns the badge flavor based on the collection status and draft state.
 *
 * @param status - The current status of the collection. Possible values include:
 *   - 'failed'
 *   - 'invalid'
 *   - 'available'
 *   - 'processing'
 *   - 'pending'
 *   - 'indexing'
 * @param is_draft - Indicates whether the collection is a draft.
 * @returns The badge flavor as a string. Possible values:
 *   - 'destructive' for 'failed' or 'invalid' status
 *   - 'informational' for 'available' drafts or 'pending' status
 *   - 'successful' for 'available' non-drafts
 *   - 'ai' for 'processing' status
 *   - 'warning' for 'indexing' status
 */
export const getFlavour = (status: string, is_draft: boolean) => {
  if (status === 'failed' || status === 'invalid') {
    return 'destructive';
  } else if (status === 'available') {
    if (is_draft) {
      return 'neutral';
    }
    return 'successful';
  } else if (status === 'processing') {
    return 'ai';
  } else if (status === 'pending') {
    return 'neutral';
  } else if (status === 'indexing') {
    return 'warning';
  }
};

/**
 * Extracts the name without the file extension
 * @param name - The full filename
 * @returns The filename without extension, or the original name if no extension exists
 * @example
 * getPureName("document.pdf") // returns "document"
 * getPureName("document") // returns "document"
 * getPureName("my.file.txt") // returns "my.file"
 */
export const getPureName = (name: string) => {
  if (!name) {
    return '';
  }
  const lastDotIndex = name.lastIndexOf('.');
  return lastDotIndex === -1 ? name : name.substring(0, lastDotIndex);
};

/**
 * Appends three dots to the end of the provided string.
 */
export function appendEllipsis(value: string): string {
  return `${value}...`;
}

export type DatasourceHealthKind = 'healthy' | 'indexing' | 'warning' | 'error' | 'failed';

/**
 * The health verdict for a datasource row, without the wording. Read by both the badge and the
 * column sort, so the two cannot disagree.
 *
 * Describes the site, not the job: a crawl can finish and still leave the site unhealthy. Counts
 * come from the v2 report, so a page the customer excluded on purpose is neither an error nor a
 * warning — which is the whole point of the report. Rows crawled before it shipped return null
 * rather than inventing a verdict.
 */
export function getDatasourceHealthKind(
  datasource: Pick<DatasourceDocument, 'status' | 'last_indexed_details'>
): { kind: DatasourceHealthKind; count: number } | null {
  const { status } = datasource;
  if (status === 'processing' || status === 'pending' || status === 'indexing') {
    return { kind: 'indexing', count: 0 };
  }

  const report = datasource.last_indexed_details?.report;
  if (!report) {
    return null;
  }

  if (report.counts.failed > 0) {
    return { kind: 'error', count: report.counts.failed };
  }

  // A crawl blocked at the door fails without failing a single url.
  const isBlocked =
    report.outcome === 'failed' || report.actions.some((action) => action.severity === 'blocking');
  if (isBlocked) {
    return { kind: 'failed', count: 0 };
  }

  // Advisory findings are worth surfacing; info ones are the crawler obeying the customer.
  const warnings = report.actions
    .filter((action) => action.severity === 'advisory')
    .reduce((total, action) => total + action.affected, 0);
  if (warnings > 0) {
    return { kind: 'warning', count: warnings };
  }

  return { kind: 'healthy', count: 0 };
}

/** The one-line health verdict shown per datasource row. */
export function getDatasourceHealth(
  datasource: Pick<DatasourceDocument, 'status' | 'last_indexed_details'>,
  t: TFunction
): { label: string; flavour: 'neutral' | 'successful' | 'warning' | 'destructive' } | null {
  const health = getDatasourceHealthKind(datasource);
  if (!health) {
    return null;
  }

  switch (health.kind) {
    case 'indexing':
      return { label: appendEllipsis(t('collections.report.indexing_now')), flavour: 'neutral' };
    case 'error':
      return {
        label: t('collections.report.n_errors', { count: health.count }),
        flavour: 'destructive',
      };
    case 'failed':
      return { label: t('collections.report.failed'), flavour: 'destructive' };
    case 'warning':
      return {
        label: t('collections.report.n_warnings', { count: health.count }),
        flavour: 'warning',
      };
    default:
      return { label: t('collections.report.healthy'), flavour: 'successful' };
  }
}

export const isDocumentRow = (item: DatasourceDocument | CollectionDocument) => {
  if (!item) {
    return false;
  }
  return !!item.datasource_id && !!item.document_id;
};

const isEditableKbCategory = (category: string) => category === 'faq' || category === 'documents';

export const getKbRowAction = (
  item: DatasourceDocument | CollectionDocument,
  category: string
): 'open' | 'edit' | null => {
  if (!isDocumentRow(item)) {
    return 'open';
  }
  return isEditableKbCategory(category) ? 'edit' : null;
};

export const getIconForDatasourceType = (type: DatasourceType | undefined) => {
  switch (type) {
    case DatasourceType.WEBSITE:
      return GlobeIcon;
    case DatasourceType.FILES:
      return FileTextIcon;
    case DatasourceType.ZENDESK_KB:
    case DatasourceType.INTERCOM_KB:
    case DatasourceType.WORKVIVO_KB:
    case DatasourceType.GDRIVE:
      return BookOpenTextIcon;
    case DatasourceType.FAQ:
      return MessagesSquareIcon;
    default:
      return null;
  }
};

export const getDatasourcesDocumentsTableIcon = (item: DatasourceDocument) => {
  // If it's not a document row, it's a folder
  if (!isDocumentRow(item)) {
    return FolderIcon;
  }

  return getIconForDatasourceType(item.datasource_type);
};

export const getContentTableIcon = (category: DatasourceTab) => {
  if (category === 'faq') {
    return MessagesSquareIcon;
  } else if (category === 'documents') {
    return FileTextIcon;
  } else if (category === 'webpages') {
    return GlobeIcon;
  } else if (category === 'external') {
    return BookOpenTextIcon;
  }

  return null;
};

export const downloadFaqsAsCsv = async (
  items: (DatasourceDocument | CollectionDocument | Document)[],
  collectionId: string,
  slug: string
) => {
  trackEvent(EventName.ClickDownloadDocument);

  try {
    const results = await Promise.all(
      items.map(async ({ document_id, datasource_id, name }) => {
        const url = `${window?.location?.origin}${documentsEndpoints.exportDocument(collectionId, datasource_id, document_id)}?account_slug=${slug}`;

        try {
          const response = await fetch(url);

          if (!response.ok) {
            return null;
          }

          return { question: name, answer: await response.text() };
        } catch {
          return null;
        }
      })
    );

    const faqPairs = results.filter(
      (result): result is { question: string; answer: string } => result !== null
    );

    if (faqPairs.length === 0) {
      toast.error(i18next.t('collections.toast_messages.download_faq_failed'));
      return;
    }

    const fileName =
      items.length === 1 ? items[0].name : `faqs_${new Date().toISOString().split('T')[0]}`;
    downloadCsv(faqPairs, fileName);
  } catch {
    toast.error(i18next.t('collections.toast_messages.download_faq_failed'));
  }
};

export const downloadDocument = (
  item: DatasourceDocument | CollectionDocument | Document,
  collectionId: string,
  slug: string,
  format?: 'csv'
) => {
  if (format === 'csv') {
    return downloadFaqsAsCsv([item], collectionId, slug);
  }

  const { document_id, datasource_id, name, type } = item;
  trackEvent(EventName.ClickDownloadDocument);

  const url = `${window?.location?.origin}/www/api/v1/collections/${collectionId}/datasources/${datasource_id}/documents/${document_id}/download?account_slug=${slug}`;

  const link = document.createElement('a');
  link.href = url;
  link.download = name || `document.${type}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Sorts DatasourceDocument objects by:
 * 1. Objects with null document_id first
 * 2. Objects with newest created first
 *
 * @param {DatasourceDocument[]} documents - Array of DatasourceDocument objects
 * @returns {DatasourceDocument[]} - Sorted array
 */
export const sortDatasourceDocuments = (documents: DatasourceDocument[]) => {
  return documents.sort((a, b) => {
    // First criterion: null document_id comes first
    const aIsNull = a.document_id === null;
    const bIsNull = b.document_id === null;

    if (aIsNull && !bIsNull) {
      return -1;
    } // a comes first
    if (!aIsNull && bIsNull) {
      return 1;
    } // b comes first

    // If both are null or both are not null, sort by created
    // Second criterion: newest created comes first
    const aCreated = a.created;
    const bCreated = b.created;

    // Handle null created values - put them at the end
    if (aCreated === null && bCreated === null) {
      return 0;
    }
    if (aCreated === null) {
      return 1;
    } // b comes first
    if (bCreated === null) {
      return -1;
    } // a comes first

    // Both have created values, compare dates (newest first, so b - a)
    return new Date(bCreated).getTime() - new Date(aCreated).getTime();
  });
};

export const isZendeskConfig = (config: Datasource['config']): config is ZendeskConfig =>
  !!config && 'subdomain' in config;
